/**
 * Search → Saved search / Buy Request handoff.
 *
 * The search engine already merged typed text + filter chips into one
 * constraint list (`parsedSearch.constraints` from search-listings). This
 * module carries THAT list forward unchanged — no second AI read, no second
 * source of truth — plus the exact UI state so the search can be reopened.
 */
import type { ListingFilters } from '@/types/search';
import { encodeFilters } from '@/lib/searchUrlState';
import { lowerToBuyRequest } from '../../supabase/functions/_shared/buyRequestLower';

export type HandoffConstraint = {
  field: string;
  op: string;
  values?: (string | number | boolean)[];
  primary_values?: string[];
  source?: string;
  confidence?: number;
};

export interface SearchSnapshot {
  intent: 'browse';
  /** What the buyer typed (may be empty when only filters are set). */
  free_text: string;
  /** The engine's full, already-merged constraint list. */
  constraints: HandoffConstraint[];
  /** Exact search-box text, for reopening. */
  ui_query: string;
  /** Exact filter chips (compact `f=` codec), for reopening. */
  ui_filters: string;
}

export function buildSearchSnapshot(
  rawQuery: string,
  filters: ListingFilters,
  engineConstraints: HandoffConstraint[] | null | undefined,
): SearchSnapshot {
  return {
    intent: 'browse',
    free_text: rawQuery.trim(),
    constraints: engineConstraints ?? [],
    ui_query: rawQuery.trim(),
    ui_filters: encodeFilters(filters, { omitKeys: ['sellerCompany'] }),
  };
}

export const snapshotHasCriteria = (s: SearchSnapshot) =>
  (s.free_text ?? '').trim().length > 0 || (s.constraints ?? []).length > 0;

const pretty = (v: unknown) => String(v).replace(/%/g, '').replace(/[-_]/g, ' ');

/** A readable sentence for the request text when only filters were used. */
export function describeSnapshot(s: SearchSnapshot): string {
  if (s.free_text?.trim()) return s.free_text.trim();
  if (s.ui_query?.trim()) return s.ui_query.trim();
  const POS = new Set(['in', 'eq', 'ilike', 'contains']);
  return (s.constraints ?? [])
    .filter((c) => POS.has(c.op))
    .map((c) => (c.primary_values?.length ? c.primary_values : c.values ?? []).map(pretty).join(' or '))
    .filter(Boolean)
    .join(', ');
}

/** Buy Request fields straight from the engine's constraints (same lowering the server uses). */
export function snapshotToRequestFields(s: SearchSnapshot) {
  return lowerToBuyRequest(s.constraints, null);
}

/** Constraints that came from fixed UI controls; kept if the buyer later edits the sentence. */
export const pinnedConstraints = (cs: HandoffConstraint[]) =>
  cs.filter((c) => c.source === 'filter_chip' || c.source === 'numeric');

/** Merge pinned chip constraints onto a fresh parse; the parse wins only on fields chips didn't set. */
export function mergePinned(parsed: HandoffConstraint[], pinned: HandoffConstraint[]): HandoffConstraint[] {
  const chipFields = new Set(pinned.map((c) => c.field));
  return [...pinned, ...parsed.filter((c) => !chipFields.has(c.field))];
}

/** Menu URL that reopens a saved search with its text AND its filter chips. */
export function savedSearchUrl(
  supplierCompanyId: string,
  menuId: string,
  rawQuery: string | null,
  parsed: Partial<SearchSnapshot> | null | undefined,
): string {
  const p = new URLSearchParams();
  const q = parsed?.ui_query ?? rawQuery ?? '';
  if (q) p.set('q', q);
  if (parsed?.ui_filters) p.set('f', parsed.ui_filters);
  const qs = p.toString();
  return `/menu/${supplierCompanyId}/${menuId}${qs ? `?${qs}` : ''}`;
}
